import { AntiMoneyLaundering, AntiMoneyLaunderingJson } from '@common/models/anti-money-laundering';
import { MissionAcceptanceStatus } from '@enums/mission-acceptance-status.enum';
import { TaxRegimeCode } from '@enums/tax-regime-code.enum';
import { VatSystemCode } from '@enums/vat-system-code.enum';
import { DateHelper } from '@helpers/date.helper';
import { HttpHelper } from '@helpers/http.helper';
import { BusinessUser, BusinessUserJson } from '@models/business-user';
import { Company, CompanyJson } from '@models/company';
import { LegalStructure, LegalStructureJson } from '@models/legal-structure';
import { MissionAcceptanceFile, MissionAcceptanceFileJson } from '@models/mission-acceptance-file';
import { Signatory, SignatoryJson } from '@models/signatory';
import { User, UserJson } from '@models/user';

import { MissionAcceptanceAcceptation, MissionAcceptanceAcceptationJson } from './mission-acceptance-acceptation';
import { MissionAcceptanceInformations, MissionAcceptanceInformationsJson } from './mission-acceptance-informations';

export interface MissionAcceptanceJson {
  id: number;
  status: MissionAcceptanceStatus;
  status_at: string;
  status_by: Partial<BusinessUserJson>;
  validated_at: string;
  validated_by: Partial<BusinessUserJson>;
  legal_structure: Partial<LegalStructureJson>;
  signatory: Partial<SignatoryJson>;
  anti_money_laundering: Partial<AntiMoneyLaunderingJson>;
  informations: Partial<MissionAcceptanceInformationsJson>;
  form: Partial<MissionAcceptanceAcceptationJson>;
  company: Partial<CompanyJson>;
  company_name: string;
  company_reference: string;
  company_legal_form: string;
  company_ape_code: string;
  company_tax_regime: TaxRegimeCode;
  company_vat_system: VatSystemCode;
  company_street: string;
  company_postal_code: string;
  company_city: string;
  contact: Partial<UserJson>;
  contact_last_name: string;
  contact_first_name: string;
  contact_email: string;
  contact_phone: string;
  contact_street: string;
  contact_postal_code: string;
  contact_city: string;
  mission_acceptance_files: MissionAcceptanceFileJson[];
}

export class MissionAcceptance {
  constructor(
    public id?: number,
    public status?: MissionAcceptanceStatus,
    public statusAt?: Date,
    public statusBy?: BusinessUser,
    public vatlidatedAt?: Date,
    public validatedBy?: BusinessUser,
    public legalStructure?: LegalStructure,
    public signatory?: Signatory,
    public antiMoneyLaundering?: AntiMoneyLaundering,
    public informations?: MissionAcceptanceInformations,
    public acceptation?: MissionAcceptanceAcceptation,
    public company?: Company,
    public companyName?: string,
    public companyReference?: string,
    public companyLegalForm?: string,
    public companyApeCode?: string,
    public companyTaxRegime?: TaxRegimeCode,
    public companyVatSystem?: VatSystemCode,
    public companyStreet?: string,
    public companyPostalCode?: string,
    public companyCity?: string,
    public contact?: User,
    public contactLastName?: string,
    public contactFirstName?: string,
    public contactEmail?: string,
    public contactPhone?: string,
    public contactStreet?: string,
    public contactPostalCode?: string,
    public contactCity?: string,
    public missionAcceptanceFiles?: MissionAcceptanceFile[]
  ) {}

  static fromJson(json: Partial<MissionAcceptanceJson>): MissionAcceptance {
    return new MissionAcceptance(
      json.id,
      json.status,
      json.status_at ? DateHelper.fromUtc(json.status_at) : null,
      json.status_by ? BusinessUser.fromJson(json.status_by) : null,
      json.validated_at ? DateHelper.fromUtc(json.validated_at) : null,
      json.validated_by ? BusinessUser.fromJson(json.validated_by) : null,
      json.legal_structure ? LegalStructure.fromJson(json.legal_structure) : null,
      json.signatory ? Signatory.fromJson(json.signatory) : null,
      json.anti_money_laundering ? AntiMoneyLaundering.fromJson(json.anti_money_laundering) : null,
      json.informations ? MissionAcceptanceInformations.fromJson(json.informations) : null,
      json.form ? MissionAcceptanceAcceptation.fromJson(json.form) : null,
      json.company ? Company.fromJson(json.company) : null,
      json.company_name,
      json.company_reference,
      json.company_legal_form,
      json.company_ape_code,
      json.company_tax_regime,
      json.company_vat_system,
      json.company_street,
      json.company_postal_code,
      json.company_city,
      json.contact ? User.fromJson(json.contact) : null,
      json.contact_last_name,
      json.contact_first_name,
      json.contact_email,
      json.contact_phone,
      json.contact_street,
      json.contact_postal_code,
      json.contact_city,
      json.mission_acceptance_files
        ? json.mission_acceptance_files.map((file: MissionAcceptanceFileJson) => MissionAcceptanceFile.fromJson(file))
        : []
    );
  }

  static toJson(
    missionAcceptance: MissionAcceptance
  ): Omit<
    MissionAcceptanceJson,
    'status' | 'status_at' | 'status_by' | 'validated_at' | 'validated_by' | 'mission_acceptance_files'
  > {
    return {
      id: HttpHelper.toJsonProperty(missionAcceptance.id),
      legal_structure: missionAcceptance.legalStructure?.id
        ? HttpHelper.toJsonId(missionAcceptance.legalStructure)
        : null,
      signatory: missionAcceptance.signatory?.id ? HttpHelper.toJsonId(missionAcceptance.signatory) : null,
      anti_money_laundering: missionAcceptance.antiMoneyLaundering
        ? AntiMoneyLaundering.toJson(missionAcceptance.antiMoneyLaundering)
        : null,
      informations: missionAcceptance.informations
        ? MissionAcceptanceInformations.toJson(missionAcceptance.informations)
        : null,
      form: missionAcceptance.acceptation ? MissionAcceptanceAcceptation.toJson(missionAcceptance.acceptation) : null,
      company: missionAcceptance.company?.id ? HttpHelper.toJsonId(missionAcceptance.company) : null,
      company_name: HttpHelper.toJsonProperty(missionAcceptance.companyName),
      company_reference: HttpHelper.toJsonProperty(missionAcceptance.companyReference),
      company_legal_form: HttpHelper.toJsonProperty(missionAcceptance.companyLegalForm),
      company_ape_code: HttpHelper.toJsonProperty(missionAcceptance.companyApeCode),
      company_tax_regime: HttpHelper.toJsonProperty(missionAcceptance.companyTaxRegime),
      company_vat_system: HttpHelper.toJsonProperty(missionAcceptance.companyVatSystem),
      company_street: HttpHelper.toJsonProperty(missionAcceptance.companyStreet),
      company_postal_code: HttpHelper.toJsonProperty(missionAcceptance.companyPostalCode),
      company_city: HttpHelper.toJsonProperty(missionAcceptance.companyCity),
      contact: missionAcceptance.contact?.id ? HttpHelper.toJsonId(missionAcceptance.contact) : null,
      contact_last_name: HttpHelper.toJsonProperty(missionAcceptance.contactLastName),
      contact_first_name: HttpHelper.toJsonProperty(missionAcceptance.contactFirstName),
      contact_email: HttpHelper.toJsonProperty(missionAcceptance.contactEmail),
      contact_phone: HttpHelper.toJsonProperty(missionAcceptance.contactPhone),
      contact_street: HttpHelper.toJsonProperty(missionAcceptance.contactStreet),
      contact_postal_code: HttpHelper.toJsonProperty(missionAcceptance.contactPostalCode),
      contact_city: HttpHelper.toJsonProperty(missionAcceptance.contactCity)
    };
  }
}
